import React from 'react'
import { useForm, FormProvider } from "react-hook-form";
import { getCountries, getStates } from '../js/address-utility';
import { type WebCustomer2 } from '../types/webshop.types';

import { useTranslation } from 'react-i18next'

type Props = {
    formRef: React.Ref<HTMLFormElement>,
    customer: WebCustomer2
}

export default function BillingAddressForm({formRef, customer}: Props) {
    const methods = useForm();
    const { register, setValue, watch, formState: { errors } } = methods;

    const countryCode = watch('countryCode', false);

    const defaultAddress = {street: '', city: '', stateCode: '', countryCode: '', postalCode: ''};

    React.useEffect(() => {
        if (!customer){
            return;
        }
        const address = customer.address || defaultAddress;
        const street = address.street || '';
        const streetSplit = street.split('\n');
        setValue('line1', streetSplit[0]);
        setValue('line2', streetSplit[1] || '');
        setValue('city', address.city);
        setValue('stateCode', address.stateCode);
        setValue('countryCode', address.countryCode);
        setValue('postalCode', address.postalCode);
    }, [customer]);

    const countries = React.useMemo(() => {
        return getCountries();
    }, []);
    const states = React.useMemo(() => {
        return getStates(countryCode);
    }, [countryCode]);

    const { t } = useTranslation('common', { useSuspense: false });

    const countryOptions = [];
    if (countries){
        for (const c of countries){
            countryOptions.push(
                <option value={c.code} key={c.code}>{c.name}</option>
            )
        }
    }

    const stateOptions = [];
    if (states){
        for (const s of states){
            stateOptions.push(
                <option value={s.code} key={s.code}>{s.name}</option>
            )
        }
    }
    
    const stateField = (
        <div className='form-field'>
            <label>{t('form.state')}</label>
            <select required className='form-select' name="state" {...register('stateCode', { required: true })}>
                {stateOptions}
            </select>
        </div>
    )

    const zipField = (
        <div className='form-field'>
            <label>{t('form.zip')}</label>
            <input required className='form-input' name="zip" {...register('postalCode', { required: true })} />
        </div>
    )

    
    const hasStates = stateOptions.length > 0;

    return (
        <form ref={formRef}>
            <div className='form-row'>
                <div className='form-field'>
                    <label>{t('form.country')}</label>
                    <select required className='form-select' name="country" {...register('countryCode', { required: true })}>
                        {countryOptions}
                    </select>
                </div>
            </div>
            <div className='form-row'>
                <div className="form-field">
                    <label>{t('form.street')}</label>
                    <input required className='form-input' name="line1" {...register('line1', { required: true })} />
                </div>
            </div>
            <div className='form-row'>
                <div className='form-field'>
                    <label>{t('form.street2')}</label>
                    <input className='form-input' name="line2" {...register('line2')} />
                </div>
            </div>
            <div className='form-row'>
                {hasStates ? stateField : zipField}
                <div className='form-field'>
                    <label>{t('form.city')}</label>
                    <input required className='form-input' name="city" {...register('city', { required: true })} />
                </div>
            </div>
            {hasStates &&(
                <div className='form-row'>
                    {zipField}      
                    <div className='form-field'></div>
                </div>
            )}
        </form>
    )
}